import "@/assets/styles/ecpay.css";
import FAIcon from "@/components/FAIcon";
import CreditCardView from "@/components/Payments/CreditCardView";
import PostLightbox from "@/components/Post/Lightbox";
import PostVideoPlayer from "@/components/Post/PostVideoPlayer";
import AuthContext from "@/context/auth";
import SubscriptionModalContext from "@/context/modal";
import apiClient from "@/libs/api";
import getStreamCDNPublicUrl from "@/libs/get-cdn-public-url";
import { default as getCDNPublicAvatarUrl, default as getPublicUrl } from "@/libs/get-public-url";
import useAPI from "@/libs/hooks/api";
// @ts-ignore
import firebase from "@/libs/firebase";
import useEcpayInit from "@/libs/hooks/useEcpayInit";
import { useIsCreator } from "@/libs/hooks/useIsCreator";
import { postPlayerStore } from "@/libs/stores/post-player.store";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { getIdToken } from "firebase/auth";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Helper function to parse aspectRatio and determine if landscape or portrait
const getAspectRatioValue = (aspectRatio) => {
  if (!aspectRatio) return 16 / 9; // default to landscape
  
  // If it's already a number (the ratio itself)
  if (typeof aspectRatio === "number") {
    return aspectRatio;
  }
  
  // If it's a string like "16:9", "4:3", "9:16", etc.
  if (typeof aspectRatio === "string") {
    const parts = aspectRatio.split(":");
    if (parts.length === 2) {
      const width = parseFloat(parts[0]);
      const height = parseFloat(parts[1]);
      if (!isNaN(width) && !isNaN(height) && height > 0) {
        return width / height;
      }
    }
  }
  
  return 16 / 9; // default to landscape
};

// Set objectFit to "contain" for images
const PreviewImage = ({ src }) => (
  <Image src={src} alt="" w="100%" h="100%" objectFit="contain" />
);

/**
 * Get image URL using proxy endpoint for clear content, fallback to blur via CDN
 * Similar to video proxy logic in post-player.store.ts
 */
const getImageUrl = async (clearPath, blurPath, creatorId, user) => {
  // blurPath always exists, normalize it for fallback
  const normalizedBlurPath = blurPath.startsWith("/") ? blurPath.slice(1) : blurPath;
  const blurUrl = getStreamCDNPublicUrl(normalizedBlurPath);

  // If no clear path, use blur
  if (!clearPath) {
    return blurUrl;
  }

  const mediaApiBaseUrl = import.meta.env.VITE_MEDIA_PROXY_SERVER;
  if (!mediaApiBaseUrl) {
    // If no proxy server, use blur from CDN
    return blurUrl;
  }

  // Get auth token if user is logged in
  let token = null;
  if (user) {
    try {
      const currentUser = firebase.auth?.currentUser;
      if (currentUser) {
        token = await getIdToken(currentUser);
      }
    } catch (error) {
      // Failed to get token, will try without token
    }
  }

  // Build proxy URL for clear content
  const mediaPath = clearPath.startsWith("/") ? clearPath : `/${clearPath}`;
  let proxyUrl = creatorId
    ? `${mediaApiBaseUrl}/media/proxy?path=${encodeURIComponent(mediaPath)}&creatorId=${creatorId}`
    : `${mediaApiBaseUrl}/media/proxy?path=${encodeURIComponent(mediaPath)}`;

  // Append token as query parameter if available
  if (token) {
    proxyUrl += `&token=${encodeURIComponent(token)}`;
  }

  // Try to access clear content via proxy
  try {
    const response = await fetch(proxyUrl, {
      method: "HEAD",
    });

    // If 403 (access denied) or any error, use blur from CDN
    if (response.status === 403 || !response.ok) {
      return blurUrl;
    }

    // If successful (200, 206, etc.), use proxy URL for clear content
    return proxyUrl;
  } catch (error) {
    // On error, fallback to blur from CDN
    return blurUrl;
  }
};

// Carousel for Post Images
const Collage = ({ 
  previews = [], 
  media = [],
  height,
  playingVideoIndex,
  onVideoClick,
  onImageClick,
  creatorId,
  isSubscribed,
  isCheckingSubscription,
  user,
  creatorSlug,
  onSubscribeClick,
  resolvedImageUrls = {},
}) => {
  const count = previews.length;
  const [index, setIndex] = useState(0);

  if (count <= 0) return null;

  // Ensure preview at current index is valid
  const current = previews[index] || previews[0];
  const currentMedia = media[index] || media[0];
  const isVideo = currentMedia?.type === "video";
  const isPlaying = playingVideoIndex === index;
  
  // Get resolved URL for images
  const getImageSrc = () => {
    if (currentMedia?.type === "image") {
      const resolvedUrl = resolvedImageUrls[index];
      // Use resolved URL if available, otherwise use blurUrl as fallback
      return resolvedUrl || current?.blurUrl || current?.url;
    }
    return current?.url || getPublicUrl(current?.thumbPath || current?.storagePath);
  };

  const src = getImageSrc();
  const blurSrc = current?.blurUrl || src;

  // Carousel controls
  const showLeft = count > 1 && index > 0;
  const showRight = count > 1 && index < count - 1;

  // Show over-count if more than 4 images
  const overCount = count > 4 ? count - 4 : 0;

  const handleClick = (e) => {
    e.stopPropagation();
    if (isVideo) {
      onVideoClick?.(index);
    } else {
      onImageClick?.(index);
    }
  };

  const handleVideoAreaClick = (e) => {
    // Stop all event propagation when video is playing
    // This prevents the parent Box's onClick from firing
    // Video controls will work normally since they're native HTML5 controls
    e.stopPropagation();
  };

  return (
    <Box 
      position="relative" 
      width="100%" 
      height={height || 300} 
      overflow="hidden"
      onClick={isVideo && isPlaying ? undefined : handleClick}
      cursor={isVideo && isPlaying ? "default" : "pointer"}
    >
      {isVideo && isPlaying ? (
        <Box
          w="100%"
          h="100%"
          onClick={handleVideoAreaClick}
        >
          <PostVideoPlayer
            asset={{
              ...currentMedia,
              creatorId: currentMedia.creatorId || creatorId,
            }}
            poster={src}
            controls
            autoplay
            muted={false}
            isActive={true}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </Box>
      ) : (
        <>
          {/* Blurred background image */}
          {blurSrc && (
            <Box
              position="absolute"
              inset={0}
              backgroundImage={`url(${blurSrc})`}
              backgroundSize="cover"
              backgroundPosition="center"
              backgroundRepeat="no-repeat"
              filter="blur(20px)"
              transform="scale(1.1)"
              zIndex={0}
            />
          )}
          <Flex w="100%" h="100%" align="center" justify="center" position="relative" zIndex={1}>
            <PreviewImage src={src} />
            {isVideo && !isPlaying && (
              <VStack
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={2}
                spacing={3}
                pointerEvents="none"
              >
                {/* SVG play button, white with subtle shadow */}
                <Box pointerEvents="none">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.45)" />
                    <polygon
                      points="26,20 26,44 46,32"
                      fill="#fff"
                      style={{
                        filter: "drop-shadow(0px 2px 6px rgba(0,0,0,0.21))"
                      }}
                    />
                  </svg>
                </Box>
                {/* Subscription button - only show if user is not subscribed */}
                {!isCheckingSubscription && !isSubscribed && user && (
                  <Button
                    size="sm"
                    colorScheme="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSubscribeClick) {
                        onSubscribeClick();
                      } else if (creatorSlug) {
                        // Fallback to navigation if no custom handler
                        window.location.href = `/c/@${creatorSlug}`;
                      }
                    }}
                    pointerEvents="auto"
                    fontSize="xs"
                    px={3}
                    py={3}
                    h="auto"
                  >
                    訂閱以觀看清晰內容
                  </Button>
                )}
              </VStack>
            )}
            {showLeft && (
              <IconButton
                aria-label="Previous"
                icon={<FAIcon type="chevron-left" />}
                size="sm"
                position="absolute"
                left={2}
                top="50%"
                transform="translateY(-50%)"
                onClick={e => {
                  e.stopPropagation();
                  setIndex(idx => (idx > 0 ? idx - 1 : 0));
                }}
                zIndex={3}
                variant="ghost"
                bg="rgba(0,0,0,0.42)"
                color="white"
                _hover={{ bg: "rgba(0,0,0,0.56)" }}
                rounded="full"
              />
            )}
            {showRight && (
              <IconButton
                aria-label="Next"
                icon={<FAIcon type="chevron-right" />}
                size="sm"
                position="absolute"
                right={2}
                top="50%"
                transform="translateY(-50%)"
                onClick={e => {
                  e.stopPropagation();
                  setIndex(idx => (idx < count - 1 ? idx + 1 : idx));
                }}
                zIndex={3}
                variant="ghost"
                bg="rgba(0,0,0,0.42)"
                color="white"
                _hover={{ bg: "rgba(0,0,0,0.56)" }}
                rounded="full"
              />
            )}
            {/* Dots */}
            {count > 1 && (
              <HStack spacing={1} position="absolute" bottom={2} left="50%" transform="translateX(-50%)" zIndex={3}>
                {[...Array(Math.min(count, 4)).keys()].map(i => (
                  <Box
                    key={i}
                    w={i === index ? "16px" : "8px"}
                    h="6px"
                    rounded="full"
                    bg={i === index ? "whiteAlpha.800" : "whiteAlpha.400"}
                    transition="all 0.15s"
                  />
                ))}
                {overCount > 0 && (
                  <Box color="white" fontSize="xs" ml={2} mr={1}>+{overCount}</Box>
                )}
              </HStack>
            )}
          </Flex>
        </>
      )}
    </Box>
  );
};

/**
 * post: {
 *  id, text, priceCents, visibility, tags[], assetsCount,
 *  cover?: { assetId, type, aspectRatio, thumbPath?, storagePath?, url? },
 *  previews?: Array<{ assetId, type, aspectRatio, url? }>
 *  isUnlocked?: boolean
 * }
 */
export default function PostCard({ post, onUnpublish }) {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { setIsSubscriptionModalOpen } = useContext(SubscriptionModalContext);
  const api = useAPI();
  const toast = useToast();
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(!!post?.isLiked);
  const [likedCount, setLikedCount] = useState(post?.likedCount || 0);
  const [isSaved, setIsSaved] = useState(!!post?.isSaved);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [playingVideoIndex, setPlayingVideoIndex] = useState(null);
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  
  // Subscription state
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [ecpayToken, setEcpayToken] = useState(null);
  const ecpayInitializedRef = useRef(false);
  const { data: creatorData } = useIsCreator();
  const isCreator = creatorData?.isCreator;
  
  // Initialize ECPay if payment provider is ECPay
  useEcpayInit();

  // Cleanup video player on unmount
  useEffect(() => {
    return () => {
      postPlayerStore.unload();
    };
  }, []);

  const openLightboxAt = useCallback((idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    postPlayerStore.unload();
    setLightboxOpen(false);
  }, []);

  const handleVideoClick = useCallback((idx) => {
    if (idx === null || idx === undefined) {
      // Stop video
      setPlayingVideoIndex(null);
      postPlayerStore.unload();
      return;
    }
    if (playingVideoIndex === idx) {
      // If already playing, stop it
      setPlayingVideoIndex(null);
      postPlayerStore.unload();
    } else {
      // Stop any other playing video
      if (playingVideoIndex !== null) {
        postPlayerStore.unload();
      }
      setPlayingVideoIndex(idx);
    }
  }, [playingVideoIndex]);

  const handleImageClick = useCallback((idx) => {
    openLightboxAt(idx);
  }, [openLightboxAt]);

  const handleUnpublish = useCallback(
    async (e) => {
      if (e) e.stopPropagation();
      onConfirmOpen();
    },
    [onConfirmOpen]
  );

  const confirmUnpublish = useCallback(
    async () => {
      if (!onUnpublish) return;
      setIsUnpublishing(true);
      try {
        await onUnpublish(post.id);
        onConfirmClose();
      } finally {
        setIsUnpublishing(false);
      }
    },
    [onUnpublish, post.id, onConfirmClose]
  );

  // Check like/save status on mount if user is logged in
  useEffect(() => {
    if (!user || !post?.id) return;
    
    Promise.all([
      api.checkPostLiked(post.id),
      api.checkPostSaved(post.id),
    ]).then(([liked, saved]) => {
      setIsLiked(liked);
      setIsSaved(saved);
    }).catch((error) => {
      console.error("Failed to check post status:", error);
    });
  }, [user, post?.id, api]);

  // Fetch subscription plan and check subscription status
  useEffect(() => {
    const creatorId = post?.creator?.id || post?.creatorId;
    if (!creatorId || creatorId.length < 15) return;

    const fetchPlanAndStatus = async () => {
      try {
        setIsCheckingSubscription(true);
        
        // Fetch subscription plan (public endpoint)
        const plan = await apiClient.getCreatorSubscriptionPlan(creatorId);
        if (!subscriptionPlan || subscriptionPlan.price !== plan.price) {
          setSubscriptionPlan(plan);
        }

        // Check subscription status if user is logged in
        if (user) {
          try {
            const status = await apiClient.checkCreatorSubscription(creatorId);
            setIsSubscribed(status?.subscribed || false);
          } catch (error) {
            // If 401 or 404, user is not subscribed
            if (error.status === 401 || error.status === 404) {
              setIsSubscribed(false);
            } else {
              console.error("Failed to check subscription status:", error);
            }
          }
        } else {
          setIsSubscribed(false);
        }
      } catch (error) {
        console.error("Failed to fetch subscription plan:", error);
      } finally {
        setIsCheckingSubscription(false);
      }
    };

    fetchPlanAndStatus();
  }, [post?.creator?.id, post?.creatorId, user?.uid, subscriptionPlan]);

  const handleCancelSubscription = useCallback(() => {
    setIsSubscribing(false);
    setSubscriptionDetails(null);
    setEcpayToken(null);
    ecpayInitializedRef.current = false;
    setIsSubscriptionModalOpen(false);
    
    // Clean up ECPay payment div content
    const ecpayDiv = document.getElementById('ECPayPayment');
    if (ecpayDiv) {
      ecpayDiv.innerHTML = '';
    }
  }, [setIsSubscriptionModalOpen]);

  const handleSubscribe = useCallback(async () => {
    const creatorId = post?.creator?.id || post?.creatorId;
    const creatorSlug = post?.creator?.slug;
    
    if (!creatorId || !user) {
      navigate("/auth/register");
      return;
    }

    // Check if user is a creator - creators cannot subscribe
    if (isCreator) {
      toast({
        title: "Creators cannot subscribe to other creators",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      // Step 1: Get payment token
      const { tradeId, token } = await apiClient.getSubscriptionPaymentToken(creatorId, `/c/@${creatorSlug}`);
      
      // Step 2: Wait for ECPay to be loaded
      let waited = 0;
      const waitPeriod = 100;
      while (!window.ECPayLoaded && waited < 10000) {
        await new Promise((resolve) => setTimeout(resolve, waitPeriod));
        waited += waitPeriod;
      }

      if (!window.ECPayLoaded) {
        throw new Error("ECPay SDK failed to load");
      }

      // Step 3: Set subscription details and token, then open modal
      setSubscriptionDetails({
        type: "creator_subscription",
        creatorId: creatorId,
        tradeId,
        ecpayToken: token,
        cost: subscriptionPlan?.price || 0,
        entry: t("payment.creator_subscription.purchase", { creator: creatorSlug }),
      });
      setEcpayToken(token);
      setIsSubscribing(true);
      setIsSubscriptionModalOpen(true);
    } catch (error) {
      console.error("Subscribe error:", error);
      toast({
        title: error.body?.error || error.message || "Failed to start subscription",
        status: "error",
        duration: 3000,
      });
    }
  }, [post?.creator?.id, post?.creator?.slug, user, isCreator, toast, t, subscriptionPlan, setIsSubscriptionModalOpen, navigate]);

  // Initialize ECPay payment UI when modal opens and token is ready
  useEffect(() => {
    if (!isSubscribing || !ecpayToken || !subscriptionDetails || ecpayInitializedRef.current) return;

    // Wait for DOM to be ready
    const initECPay = () => {
      const ecpayDiv = document.getElementById('ECPayPayment');
      if (!ecpayDiv) {
        // Retry after a short delay if div doesn't exist yet
        setTimeout(initECPay, 50);
        return;
      }

      // Clean up any existing content first
      ecpayDiv.innerHTML = '';

      // Mark as initialized to prevent multiple calls
      ecpayInitializedRef.current = true;

      const cb = function (errMsg) {
        if (errMsg) {
          toast({
            title: errMsg || "Failed to initialize payment",
            status: "error",
            duration: 3000,
          });
          handleCancelSubscription();
        }
      };

      try {
        window.ECPay.createPayment(ecpayToken, window.ECPay.Language.zhTW, cb, 'V2');
      } catch (e) {
        console.error("ECPay initialization error:", e);
        toast({
          title: "Failed to initialize payment",
          status: "error",
          duration: 3000,
        });
        handleCancelSubscription();
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(initECPay);
    });
  }, [isSubscribing, ecpayToken, subscriptionDetails, toast, handleCancelSubscription]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      ecpayInitializedRef.current = false;
      const ecpayDiv = document.getElementById('ECPayPayment');
      if (ecpayDiv) {
        ecpayDiv.innerHTML = '';
      }
    };
  }, []);

  const toggleLiked = useCallback(
    async (e) => {
      if (e) e.stopPropagation();
      if (!user) return;
      const action = isLiked ? api.unlikePost : api.likePost;
      try {
        await action(post.id);
        setIsLiked(!isLiked);
        setLikedCount((prev) => prev + (isLiked ? -1 : 1));
      } catch (error) {
        console.error("Failed to toggle like:", error);
        toast({
          title: "Error",
          status: "error",
        });
      }
    },
    [api, post.id, isLiked, toast, user],
  );

  const toggleSaved = useCallback(
    async (e) => {
      if (e) e.stopPropagation();
      if (!user) return;
      const action = isSaved ? api.unsavePost : api.savePost;
      try {
        await action(post.id);
        setIsSaved(!isSaved);
      } catch (error) {
        console.error("Failed to toggle save:", error);
        toast({
          title: "Error",
          status: "error",
        });
      }
    },
    [api, post.id, isSaved, toast, user],
  );

  const previews = useMemo(() => {
    const media = post?.media || [];
    if (media.length === 0 && post?.previews) return post.previews;
    return media.slice(0, 4).map((item) => {
      if (item.type === "image") {
        // Return structure with paths - URLs will be resolved asynchronously
        return {
          type: "image",
          clearPath: item.clear,
          blurPath: item.blur,
          aspectRatio: item.aspectRatio || "16:9",
          url: null, // Will be set asynchronously
          blurUrl: item.blur ? (() => {
            const normalizedBlurPath = item.blur.startsWith("/") ? item.blur.slice(1) : item.blur;
            return getStreamCDNPublicUrl(normalizedBlurPath);
          })() : undefined,
        };
      }
      // For videos: prefer user-uploaded coverPhoto, then thumbnail.clear, fallback to thumbnail.blur
      const coverPhotoPath = item.coverPhoto;
      const clearThumbPath = item.thumbnail?.clear;
      const blurThumbPath = item.thumbnail?.blur;
      const thumbPath = coverPhotoPath || clearThumbPath || blurThumbPath;
      if (!thumbPath) return { url: undefined, blurUrl: undefined, aspectRatio: item.aspectRatio || "16:9" };
      // Normalize paths (remove leading slash if present)
      const normalizedCoverPhotoPath = coverPhotoPath ? (coverPhotoPath.startsWith("/") ? coverPhotoPath.slice(1) : coverPhotoPath) : null;
      const normalizedClearThumbPath = clearThumbPath ? (clearThumbPath.startsWith("/") ? clearThumbPath.slice(1) : clearThumbPath) : null;
      const normalizedBlurThumbPath = blurThumbPath ? (blurThumbPath.startsWith("/") ? blurThumbPath.slice(1) : blurThumbPath) : null;
      return {
        url: normalizedCoverPhotoPath ? getStreamCDNPublicUrl(normalizedCoverPhotoPath) : (normalizedClearThumbPath ? getStreamCDNPublicUrl(normalizedClearThumbPath) : undefined),
        blurUrl: normalizedBlurThumbPath ? getStreamCDNPublicUrl(normalizedBlurThumbPath) : (normalizedCoverPhotoPath ? getStreamCDNPublicUrl(normalizedCoverPhotoPath) : (normalizedClearThumbPath ? getStreamCDNPublicUrl(normalizedClearThumbPath) : undefined)),
        aspectRatio: item.aspectRatio || "16:9",
      };
    });
  }, [post?.media, post?.previews]);

  // State for resolved image URLs
  const [resolvedImageUrls, setResolvedImageUrls] = useState({});

  // Create a stable key for previews to prevent unnecessary re-fetches
  const previewsKey = useMemo(() => {
    return previews
      .map((p) => (p.type === "image" ? p.clearPath : ""))
      .join("|");
  }, [previews]);

  // Resolve image URLs asynchronously
  useEffect(() => {
    const resolveImageUrls = async () => {
      const creatorId = post?.creator?.id || post?.creatorId;
      const newUrls = {};

      for (let i = 0; i < previews.length; i++) {
        const preview = previews[i];
        if (preview.type === "image" && preview.clearPath) {
          const url = await getImageUrl(preview.clearPath, preview.blurPath, creatorId, user);
          newUrls[i] = url;
        }
      }

      setResolvedImageUrls(newUrls);
    };

    resolveImageUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewsKey, post?.creator?.id, post?.creatorId, user]);

  // Determine carousel height based on first media item's aspectRatio
  const carouselHeight = useMemo(() => {
    if (previews.length === 0) return 300; // default
    
    const firstMedia = post?.media?.[0];
    if (!firstMedia) return 300;
    
    const aspectRatioValue = getAspectRatioValue(firstMedia.aspectRatio);
    // If aspectRatio >= 1, it's landscape (width >= height)
    // If aspectRatio < 1, it's portrait (height > width)
    return aspectRatioValue >= 1 ? 300 : 500;
  }, [post?.media, previews.length]);

  const publishTime = useMemo(() => {
    if (!post?.publishedAt && !post?.createdAt) return null;
    const raw =
      typeof post?.publishedAt === "number" || typeof post?.publishedAt === "string"
        ? post.publishedAt
        : post?.publishedAt?._seconds;
    const fallback =
      typeof post?.createdAt === "number" || typeof post?.createdAt === "string"
        ? post.createdAt
        : post?.createdAt?._seconds;
    const value = raw ?? fallback;
    if (!value) return null;
    if (typeof value === "string") {
      return dayjs(value).format("MMM D, HH:mm");
    }
    if (typeof value === "number") {
      const date = value > 1e12 ? dayjs(value) : dayjs.unix(value);
      return date.format("MMM D, HH:mm");
    }
    return null;
  }, [post?.publishedAt, post?.createdAt]);

  // Parse mentions from text and match with post.mentions
  const textWithMentions = useMemo(() => {
    if (!post?.text) return null;
    const text = post.text;
    const mentions = post?.mentions || [];
    if (mentions.length === 0) return null;

    // Find all @mentions in text using regex
    const MENTION_REGEX = /@([a-zA-Z0-9._-]+)/g;
    const matches = [];
    let match;
    
    while ((match = MENTION_REGEX.exec(text)) !== null) {
      const slug = match[1].toLowerCase();
      const mention = mentions.find(
        (m) => m.slug?.toLowerCase() === slug || m.displayName?.toLowerCase() === slug
      );
      if (mention) {
        matches.push({
          startIndex: match.index,
          endIndex: match.index + match[0].length,
          mention,
        });
      }
    }

    if (matches.length === 0) return null;

    // Sort by start position
    matches.sort((a, b) => a.startIndex - b.startIndex);

    // Build parts array
    const parts = [];
    let lastIndex = 0;

    matches.forEach((match, index) => {
      // Add text before mention
      if (match.startIndex > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.startIndex),
          key: `text-${index}`,
        });
      }

      // Add mention
      parts.push({
        type: 'mention',
        content: text.slice(match.startIndex, match.endIndex),
        mention: match.mention,
        key: `mention-${index}`,
      });

      lastIndex = match.endIndex;
    });

    // Add remaining text after last mention
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex),
        key: 'text-end',
      });
    }

    return parts;
  }, [post?.text, post?.mentions]);

  const creatorPicture = useMemo(() => {
    if (!post?.creator?.picture) return undefined;
    return getPublicUrl(post.creator.picture);
  }, [post?.creator?.picture]);

  return (
    <Box
      borderWidth="1px"
      borderColor="rgba(255,255,255,0.08)"
      rounded="md"
      overflow="hidden"
      bg="#0f0f0f"
    >
      {/* Header with creator info and publish time */}
      <Box p={4} pb={3}>
        <Flex justify="space-between" align="center">
          <HStack 
            spacing={3} 
            cursor="pointer"
            onClick={(e) => {
              e.stopPropagation();
              const slug = post?.creator?.slug;
              if (slug) {
                navigate(`/c/@${slug}`);
              }
            }}
            _hover={{ opacity: 0.8 }}
            transition="opacity 0.2s"
          >
            <Avatar size="sm" src={creatorPicture} />
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" fontWeight="semibold" color="white">
                {post?.creator?.displayName || "Creator"}
              </Text>
              <Text fontSize="xs" color="gray.400">
                @{post?.creator?.slug || "unknown"}
              </Text>
            </VStack>
          </HStack>
          {publishTime && (
            <Text fontSize="xs" color="gray.500" onClick={(e) => {
              e.stopPropagation();
              navigate(`/posts/${post.id}`);
            }}>
              {publishTime}
            </Text>
          )}
        </Flex>
      </Box>

      {/* Post text above media */}
      {post?.text && (
        <Box px={4} pt={4} pb={2}>
          <Text noOfLines={3} color="white" fontSize="sm" wordBreak="break-word">
            {textWithMentions ? (
              textWithMentions.map((part) => {
                if (part.type === 'mention') {
                  const creatorSlug = part.mention.slug;
                  return (
                    <Text
                      key={part.key}
                      as="span"
                      color="secondary.100"
                      fontWeight="bold"
                      px={0.5}
                      py={0.5}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (creatorSlug) {
                          navigate(`/c/@${creatorSlug}`);
                        }
                      }}
                      title={part.mention.displayName}
                    >
                      {part.content}
                    </Text>
                  );
                }
                return <span key={part.key}>{part.content}</span>;
              })
            ) : (
              post.text
            )}
          </Text>
        </Box>
      )}


      {post?.mentions && post.mentions.length > 0 && (
        <Box px={4} py={3}>
          <Flex gap={0} wrap="wrap" align="center">
            {post.mentions.map((mention, idx) => (
              <Flex align="center" key={mention.id}>
                <Text
                  color="secondary.500"
                  as="span"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (mention.slug) {
                      navigate(`/c/@${mention.slug}`);
                    }
                  }}
                >
                  {mention.displayName}
                </Text>
                <Text color="whiteAlpha.600" fontSize="2xs" as="span" px={1}>
                  @{mention.slug}
                </Text>
                {idx !== post.mentions.length - 1 && (
                  <Box
                    height="18px"
                    width="1.5px"
                    bg="gray.500"
                    mx={2}
                    borderRadius="full"
                  />
                )}
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {/* Media */}
      {previews.length > 0 && (
        <Collage 
          previews={previews} 
          media={post?.media || []}
          height={carouselHeight}
          playingVideoIndex={playingVideoIndex}
          onVideoClick={handleVideoClick}
          onImageClick={handleImageClick}
          creatorId={post?.creator?.id || post?.creatorId}
          isSubscribed={isSubscribed}
          isCheckingSubscription={isCheckingSubscription}
          user={user}
          creatorSlug={post?.creator?.slug}
          onSubscribeClick={handleSubscribe}
          resolvedImageUrls={resolvedImageUrls}
        />
      )}

      {/* Lightbox for images */}
      <PostLightbox
        assets={post?.media || []}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        startIndex={lightboxIndex}
        creatorId={post?.creator?.id || post?.creatorId}
      />

      <Box p={4}>
        <Flex gap={2} wrap="wrap">
          {(post?.tags || []).map((tag) => (
            <Badge key={tag} colorScheme="secondary" variant="subtle">
              #{tag}
            </Badge>
          ))}
        </Flex>
        <Flex mt={3} align="center" justify="space-between">
          <Text fontSize="sm" color="gray.400">
            {(post?.media?.length ?? post?.assetsCount ?? 0)} media
          </Text>
          <HStack spacing={2}>
            {user && (
              <>
                <IconButton
                  aria-label="Like"
                  icon={
                    <FAIcon
                      variant={isLiked ? "solid" : "regular"}
                      type="heart"
                      color={isLiked ? "red.400" : "gray.400"}
                    />
                  }
                  size="sm"
                  variant="ghost"
                  onClick={toggleLiked}
                  color={isLiked ? "red.400" : "gray.400"}
                />
                <Text fontSize="sm" color="gray.400">
                  {likedCount}
                </Text>
                <IconButton
                  aria-label="Save"
                  icon={
                    <FAIcon
                      variant={isSaved ? "solid" : "regular"}
                      type="bookmark"
                      color={isSaved ? "blue.300" : "gray.400"}
                    />
                  }
                  size="sm"
                  variant="ghost"
                  onClick={toggleSaved}
                  color={isSaved ? "blue.300" : "gray.400"}
                />
              </>
            )}
            {onUnpublish && (
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={handleUnpublish}
                isLoading={isUnpublishing}
              >
                下架
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>

      {post?.mentions && post.mentions.length > 0 && (
        <Box
          px={4}
          py={3}
          bg="rgba(255,255,255,0.03)"
          borderTop="1px solid rgba(255,255,255,0.08)"
        >
          <VStack spacing={3} align="stretch">
            {post.mentions.map((mention) => {
              const picturePath = mention.picture
                ? mention.picture
                : null;
              const coverSrc = picturePath
                ? getCDNPublicAvatarUrl(picturePath, "512")
                : undefined;
              const avatarSrc = picturePath ? getCDNPublicAvatarUrl(picturePath, "128") : undefined;
              const creatorSlug = mention.slug;
              return (
                <Box
                  key={mention.id || creatorSlug}
                  rounded="lg"
                  overflow="hidden"
                  bg="#101014"
                  cursor="pointer"
                  _hover={{
                    backgroundImage: coverSrc,
                    backgroundSize: "cover",
                    backgroundPosition: "bottom",
                    backgroundRepeat: "no-repeat",
                  }}
                  transition="all 0.2s"
                  position="relative"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (creatorSlug) {
                      navigate(`/c/${creatorSlug}`);
                    }
                  }}
                  // Set container background with coverSrc, fallback to flat if missing
                  sx={{
                    backgroundImage: coverSrc,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                  p={4}
                  display="flex"
                  alignItems="flex-end"
                  minH="0"
                >
                  {/* Semi-transparent dim overlay */}
                  <Box
                    position="absolute"
                    left={0}
                    right={0}
                    bottom={0}
                    height="50%"
                    bg={coverSrc ? "rgba(0,0,0,0.5)" : "none"}
                    zIndex={1}
                  />
                  {/* Avatar, display name, and handle */}
                  <Flex
                    position="relative"
                    zIndex={2}
                    align="flex-end"
                    w="100%"
                  >
                    <Avatar size="xl" src={avatarSrc} mr={3} alignSelf="flex-end" />
                    <Box pb={1} flex="1" minW={0}>
                      <Text
                        fontWeight="bold"
                        color="white"
                        fontSize="2xl"
                        mb={0}
                        lineHeight="short"
                      >
                        {mention.displayName}
                      </Text>
                      <Text fontSize="md" color="whiteAlpha.700" lineHeight="short">
                        @{creatorSlug}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              );
            })}
          </VStack>
        </Box>
      )}

      {/* Subscription Payment Modal */}
      <Modal isOpen={isSubscribing} onClose={handleCancelSubscription} isCentered size="xl" closeOnOverlayClick={!subscriptionDetails}>
        <ModalOverlay />
        <ModalBody>
          <ModalContent bg="primary.100" color="white" borderRadius={12} border="1px solid" borderColor="secondary.500" p={6}>
            <CreditCardView
              active={true}
              details={subscriptionDetails || {}}
              onCancel={handleCancelSubscription}
            />
          </ModalContent>
        </ModalBody>
      </Modal>

      {/* Unpublish Confirmation Modal */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="primary.100" color="white">
          <ModalHeader>確認下架</ModalHeader>
          <ModalBody>
            <Text>確定要下架此貼文嗎？下架後將不會顯示在您的個人頁面。</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onConfirmClose}>
              取消
            </Button>
            <Button
              colorScheme="red"
              onClick={confirmUnpublish}
              isLoading={isUnpublishing}
            >
              確認下架
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

